import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета НДФЛ
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущей суммы дохода
  const incomeAmount = parseFloat(formData.incomeAmount) || 0;
  
  // Обработчик изменения суммы дохода
  const handleIncomeAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      incomeAmount: newValue
    }));
  };
  
  // Обработчик изменения типа дохода
  const handleIncomeTypeChange = (incomeType) => {
    setFormData(prev => ({
      ...prev,
      incomeType
    }));
  };
  
  // Обработчик изменения периода дохода
  const handlePaymentPeriodChange = (paymentPeriod) => {
    setFormData(prev => ({
      ...prev,
      paymentPeriod
    }));
  };
  
  // Обработчик изменения налогового статуса
  const handleTaxResidencyChange = (taxResidency) => {
    setFormData(prev => ({
      ...prev,
      taxResidency,
      // Сбрасываем вычеты для нерезидентов
      includeDeductions: taxResidency === 'resident' ? prev.includeDeductions : false
    }));
  };
  
  // Обработчик изменения статуса ИП
  const handleIpChange = (isIp) => {
    setFormData(prev => ({
      ...prev,
      isIp,
      isSelfEmployed: isIp ? false : prev.isSelfEmployed, // Нельзя одновременно быть ИП и самозанятым
      incomeType: isIp ? 'business' : prev.incomeType // Для ИП автоматически устанавливаем тип дохода "бизнес"
    }));
  };
  
  // Обработчик изменения статуса самозанятого
  const handleSelfEmployedChange = (isSelfEmployed) => {
    setFormData(prev => ({
      ...prev,
      isSelfEmployed,
      isIp: isSelfEmployed ? false : prev.isIp, // Нельзя одновременно быть ИП и самозанятым
      includeDeductions: isSelfEmployed ? false : prev.includeDeductions // Для самозанятых нет вычетов
    }));
  };
  
  // Предопределенные суммы дохода для быстрого выбора
  const predefinedAmounts = [
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '6000000', label: '6 млн' }
  ];
  
  // Основные типы доходов
  const mainIncomeTypes = [
    { id: 'salary', name: 'Зарплата' },
    { id: 'business', name: 'Бизнес' },
    { id: 'dividend', name: 'Дивиденды' },
    { id: 'rent', name: 'Аренда' }
  ];
  
  // Функция расчета
  const calculateTax = () => {
    // Клик по кнопке Submit на основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Проверка, есть ли ошибки в форме
  const hasErrors = Object.keys(errors).length > 0;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый расчет НДФЛ
      </h3>
      
      <div className="space-y-5">
        {/* Сумма дохода */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="incomeAmountSlider" className="text-gray-600 dark:text-gray-400">Сумма дохода</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(incomeAmount, 0)}</span>
          </div>
          <input
            type="range"
            id="incomeAmountSlider"
            min="10000"
            max="10000000"
            step="10000"
            value={incomeAmount}
            onChange={handleIncomeAmountChange}
            aria-label="Сумма дохода"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, incomeAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.incomeAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип дохода */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Тип дохода
          </div>
          <div className="grid grid-cols-2 gap-2">
            {mainIncomeTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleIncomeTypeChange(type.id)}
                disabled={formData.isIp || formData.isSelfEmployed}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.incomeType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  } 
                  ${(formData.isIp || formData.isSelfEmployed) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Период дохода */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Период
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handlePaymentPeriodChange('annual')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.paymentPeriod === 'annual' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Годовой
            </button>
            <button
              type="button"
              onClick={() => handlePaymentPeriodChange('monthly')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.paymentPeriod === 'monthly' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Месячный
            </button>
            <button
              type="button"
              onClick={() => handlePaymentPeriodChange('daily')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.paymentPeriod === 'daily' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Дневной
            </button>
          </div>
        </div>
        
        {/* Налоговый статус */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Налоговый статус
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleTaxResidencyChange('resident')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.taxResidency === 'resident' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Резидент РФ
            </button>
            <button
              type="button"
              onClick={() => handleTaxResidencyChange('nonresident')}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.taxResidency === 'nonresident' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Нерезидент РФ
            </button>
          </div>
        </div>
        
        {/* Статус налогоплательщика */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Специальный статус
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleIpChange(!formData.isIp)}
              disabled={formData.isSelfEmployed}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.isIp 
                  ? 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                } 
                ${formData.isSelfEmployed ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              ИП
            </button>
            <button
              type="button"
              onClick={() => handleSelfEmployedChange(!formData.isSelfEmployed)}
              disabled={formData.isIp}
              className={`p-2 text-xs rounded-lg border transition-colors
                ${formData.isSelfEmployed 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }
                ${formData.isIp ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              Самозанятый
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTax}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || hasErrors}
          isLoading={isCalculating}
        >
          Рассчитать НДФЛ
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;